// Utility functions for handling links in comments

// YouTube URL patterns
const YOUTUBE_PATTERNS = [
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/gi,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^"&?\/\s]{11})/gi,
    /(?:https?:\/\/)?youtu\.be\/([^"&?\/\s]{11})/gi
];

// General URL pattern
const URL_PATTERN = /(https?:\/\/(?:[-\w.])+(?:\:[0-9]+)?(?:\/(?:[\w\/_.])*(?:\?(?:[\w&=%.])*)?(?:\#(?:[\w.])*)?)?)/gi;

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if not found
 */
export const extractYouTubeVideoId = (url) => {
    for (const pattern of YOUTUBE_PATTERNS) {
        const match = pattern.exec(url);
        if (match && match[1]) {
            pattern.lastIndex = 0; // Reset regex state
            return match[1];
        }
        pattern.lastIndex = 0; // Reset regex state
    }
    return null;
};

/**
 * Check if URL is a YouTube video
 * @param {string} url - URL to check
 * @returns {boolean} - True if YouTube video URL
 */
export const isYouTubeUrl = (url) => {
    return extractYouTubeVideoId(url) !== null;
};

/**
 * Check if URL is a video URL (YouTube, Vimeo, etc.)
 * @param {string} url - URL to check
 * @returns {boolean} - True if video URL
 */
export const isVideoUrl = (url) => {
    const videoPatterns = [
        /youtube\.com/i,
        /youtu\.be/i,
        /vimeo\.com/i,
        /dailymotion\.com/i,
        /twitch\.tv/i,
        /streamable\.com/i
    ];
    
    return videoPatterns.some(pattern => pattern.test(url));
};

/**
 * Check if URL is a design/collaboration tool
 * @param {string} url - URL to check
 * @returns {boolean} - True if design tool URL
 */
export const isDesignUrl = (url) => {
    const designPatterns = [
        /figma\.com/i,
        /sketch\.com/i,
        /invisionapp\.com/i,
        /adobe\.com/i,
        /canva\.com/i,
        /miro\.com/i,
        /whimsical\.com/i
    ];
    
    return designPatterns.some(pattern => pattern.test(url));
};

/**
 * Check if URL is a code/development tool
 * @param {string} url - URL to check
 * @returns {boolean} - True if development tool URL
 */
export const isDevUrl = (url) => {
    const devPatterns = [
        /github\.com/i,
        /gitlab\.com/i,
        /bitbucket\.org/i,
        /codepen\.io/i,
        /jsfiddle\.net/i,
        /codesandbox\.io/i,
        /repl\.it/i,
        /stackblitz\.com/i
    ];
    
    return devPatterns.some(pattern => pattern.test(url));
};

/**
 * Check if URL is a document/file sharing platform
 * @param {string} url - URL to check
 * @returns {boolean} - True if document platform URL
 */
export const isDocumentUrl = (url) => {
    const docPatterns = [
        /docs\.google\.com/i,
        /drive\.google\.com/i,
        /dropbox\.com/i,
        /onedrive\.live\.com/i,
        /sharepoint\.com/i,
        /notion\.so/i,
        /airtable\.com/i,
        /trello\.com/i
    ];
    
    return docPatterns.some(pattern => pattern.test(url));
};

/**
 * Get platform type for URL
 * @param {string} url - URL to check
 * @returns {string} - Platform type
 */
export const getPlatformType = (url) => {
    if (isYouTubeUrl(url)) return 'youtube';
    if (isVideoUrl(url)) return 'video';
    if (isDesignUrl(url)) return 'design';
    if (isDevUrl(url)) return 'development';
    if (isDocumentUrl(url)) return 'document';
    return 'general';
};

/**
 * Extract all URLs from text
 * @param {string} text - Text to search for URLs
 * @returns {Array} - Array of URL objects with position info
 */
export const extractUrls = (text) => {
    if (!text) return [];
    
    const urls = [];
    let match;
    
    // Reset regex state
    URL_PATTERN.lastIndex = 0;
    
    while ((match = URL_PATTERN.exec(text)) !== null) {
        const url = match[0];
        const startIndex = match.index;
        const endIndex = startIndex + url.length;
        
        urls.push({
            url,
            startIndex,
            endIndex,
            isYouTube: isYouTubeUrl(url),
            isVideo: isVideoUrl(url),
            isDesign: isDesignUrl(url),
            isDev: isDevUrl(url),
            isDocument: isDocumentUrl(url),
            platformType: getPlatformType(url),
            videoId: isYouTubeUrl(url) ? extractYouTubeVideoId(url) : null
        });
    }
    
    return urls;
};

/**
 * Get YouTube thumbnail URL
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - Thumbnail quality (default, medium, high, standard, maxres)
 * @returns {string} - Thumbnail URL
 */
export const getYouTubeThumbnail = (videoId, quality = 'medium') => {
    const qualityMap = {
        default: 'default',
        medium: 'mqdefault',
        high: 'hqdefault',
        standard: 'sddefault',
        maxres: 'maxresdefault'
    };
    
    const thumbnailQuality = qualityMap[quality] || qualityMap.medium;
    return `https://img.youtube.com/vi/${videoId}/${thumbnailQuality}.jpg`;
};

/**
 * Get video title from URL (simplified version - in real app you might want to use YouTube API)
 * @param {string} url - Video URL
 * @returns {Promise<string>} - Video title
 */
export const getVideoTitle = async (url) => {
    // For now, return a simple title based on URL
    // In a real application, you'd want to use the YouTube API or oEmbed
    if (isYouTubeUrl(url)) {
        const videoId = extractYouTubeVideoId(url);
        return `YouTube Video (${videoId})`;
    }
    
    return 'Video';
};

/**
 * Parse text and replace URLs with link objects
 * @param {string} text - Text to parse
 * @returns {Array} - Array of text parts and link objects
 */
export const parseTextWithLinks = (text) => {
    if (!text) return [{ type: 'text', content: text }];
    
    const urls = extractUrls(text);
    if (urls.length === 0) {
        return [{ type: 'text', content: text }];
    }
    
    const parts = [];
    let lastIndex = 0;
    
    urls.forEach((urlInfo) => {
        // Add text before the URL
        if (urlInfo.startIndex > lastIndex) {
            const textBefore = text.substring(lastIndex, urlInfo.startIndex);
            if (textBefore) {
                parts.push({ type: 'text', content: textBefore });
            }
        }
        
        // Add the URL as a link object
        parts.push({
            type: 'link',
            content: urlInfo.url,
            url: urlInfo.url,
            isYouTube: urlInfo.isYouTube,
            isVideo: urlInfo.isVideo,
            isDesign: urlInfo.isDesign,
            isDev: urlInfo.isDev,
            isDocument: urlInfo.isDocument,
            platformType: urlInfo.platformType,
            videoId: urlInfo.videoId
        });
        
        lastIndex = urlInfo.endIndex;
    });
    
    // Add remaining text after the last URL
    if (lastIndex < text.length) {
        const textAfter = text.substring(lastIndex);
        if (textAfter) {
            parts.push({ type: 'text', content: textAfter });
        }
    }
    
    return parts;
};

/**
 * Get domain from URL for display
 * @param {string} url - URL to extract domain from
 * @returns {string} - Domain name
 */
export const getDomainFromUrl = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch (error) {
        return url;
    }
};

/**
 * Validate if URL is safe to display
 * @param {string} url - URL to validate
 * @returns {boolean} - True if URL is safe
 */
export const isSafeUrl = (url) => {
    try {
        const urlObj = new URL(url);
        // Allow http and https protocols
        return ['http:', 'https:'].includes(urlObj.protocol);
    } catch (error) {
        return false;
    }
};
